/**
 * Market API Service
 * 
 * Service for fetching opinion market data from the backend.
 * Backend serves curated markets that have been registered after on-chain creation.
 */

import { Market, MarketStatus, MarketApiResponse } from '../types/market';
import { getBackendUrl } from '../utils/backendUrl';
import { formatTimeUntil } from '../utils/formatters';

// Pagination response type
interface PaginatedResponse<T> {
  success: boolean;
  data?: T[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
  error?: string;
}

class MarketApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBackendUrl();
  }

  /**
   * Get paginated list of registered markets
   * 
   * @param options - Query options
   * @param options.page - Page number (default: 1)
   * @param options.limit - Items per page (default: 20)
   * @param options.status - Filter: 'active', 'upcoming', 'closed', 'all'
   */
  async getMarkets(options?: { 
    page?: number; 
    limit?: number; 
    status?: 'active' | 'upcoming' | 'closed' | 'all';
  }): Promise<PaginatedResponse<Market>> {
    try {
      const params = new URLSearchParams();
      if (options?.page) params.set('page', options.page.toString());
      if (options?.limit) params.set('limit', options.limit.toString());
      if (options?.status) params.set('status', options.status);

      const url = `${this.baseUrl}/api/market/list${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      const result = await response.json();
      
      return {
        success: result.success,
        data: result.data || [],
        pagination: result.pagination,
        error: result.error
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        error: error.message || 'Failed to fetch markets'
      };
    }
  }

  /**
   * Get specific market by address (PDA)
   */
  async getMarketByAddress(address: string): Promise<MarketApiResponse<Market | null>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/market/address/${address}`);
      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          data: null,
          error: result.error || 'Market not found'
        };
      }
      
      return {
        success: result.success,
        data: result.data || null,
        error: result.error
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        error: error.message || 'Failed to fetch market'
      };
    }
  }

  /**
   * Register a market after on-chain creation
   * 
   * The transaction signature proves the creator authorized the market
   * (they signed the tx to create it), so no additional auth needed.
   * 
   * @param signature - Transaction signature of CreateMarket tx
   * @param imageUrl - Optional image URL for the market
   */
  async registerMarket(
    signature: string, 
    imageUrl?: string
  ): Promise<MarketApiResponse<Market>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/market/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signature,
          image_url: imageUrl
        })
      });

      const result = await response.json();
      
      return {
        success: result.success,
        data: result.data,
        error: result.error,
        message: result.message
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register market'
      };
    }
  }

  /**
   * Get the currently active market (legacy - for backward compatibility)
   * @deprecated Use getMarkets() instead
   */
  async getCurrentMarket(): Promise<MarketApiResponse<Market | null>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/market/current`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch current market'
      };
    }
  }

  /**
   * Get specific market by ID (legacy)
   * @deprecated Use getMarketByAddress() instead
   */
  async getMarketById(marketId: number): Promise<MarketApiResponse<Market>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/market/${marketId}`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch market'
      };
    }
  }

  /**
   * Get all markets (for checking user positions)
   * IMPORTANT: Uses status='all' because user might have positions in ended markets
   * that they need to claim winnings from
   */
  async getAllMarkets(): Promise<MarketApiResponse<Market[]>> {
    const result = await this.getMarkets({ 
      limit: 500,      // Higher limit to get all markets
      status: 'all'    // Must include ended markets for position checking
    });
    return {
      success: result.success,
      data: result.data,
      error: result.error
    };
  }

  /**
   * Helper: Determine market status based on timestamps
   */
  getMarketStatus(market: Market): MarketStatus {
    const now = Date.now();
    const startsAt = new Date(market.starts_at).getTime();
    const endsAt = new Date(market.ends_at).getTime();

    if (now < startsAt) return 'upcoming';
    if (now >= startsAt && now < endsAt) return 'active';
    return 'closed';
  }

  /**
   * Helper: Format time remaining until market starts or ends
   */
  getTimeDisplay(market: Market): string {
    const status = this.getMarketStatus(market);

    if (status === 'upcoming') {
      return `Starts in ${formatTimeUntil(market.starts_at)}`;
    }

    if (status === 'active') {
      return `${formatTimeUntil(market.ends_at)} left`;
    }

    return 'Ended';
  }
}

// Export singleton instance
export const marketApiService = new MarketApiService();
